import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { StoreService } from 'src/app/store.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { FormBuilder, Validators } from '@angular/forms';
import {
  StoreNamePatternValidator,
  SubDomainPatternValidator,
} from 'src/app/core/constants/custom-validators.constants';
import { combineLatest } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { DataService } from 'src/app/data.service';
import { DealerService } from 'src/app/dealer.service';
import { StoreInfoService } from 'src/app/store-info.service';

@Component({
  selector: 'app-create-store',
  templateUrl: './create-store.component.html',
  styleUrls: ['./create-store.component.scss'],
})
export class DashboardCreateStoreComponent implements OnInit {
  @ViewChild('restrictedWordsModalContent') restrictedWordsModalContent;
  bearer;
  store_id;
  isInitialLoading = true;
  storeSubdomain = environment.storeSubdomain;

  restrictedWords = [];
  restrictedWordsLoaded = false;

  pageStep: number = 1;
  countryList = [];
  languageList = [];
  checkingSubdomain = false;
  isLoadingCreateStore = false;
  subdomainValid = false;
  planId = 1;

  dealerData: any = {};
  dealerCredits = 0;
  dealerMinimumCredit = 10;
  dealerCreditsValid = false;
  dealerCreditsLoaded = false;
  dealerCreditsLoading = false;

  aiGenerateStoreContent = false;
  private aiGenerateStoreContentTouched = false;
  aiGenerateStoreContentFeatureEnabled = true;

  aiContentLanguageCodes: string[] = [];

  private lastAutoSubdomain = '';
  private subdomainManuallyEdited = false;

  showAdvancedRegional = false;

  createStorePages = [
    {
      stepNumber: 1,
      titleText: 'Store Information',
      subText: 'Give your store a name and customize its web address',
      icon: 'fi-rr-shop',
    },
    {
      stepNumber: 2,
      titleText: 'AI Automation & Launch',
      subText: 'Enable AI content generation and review your store details',
      icon: 'fi-rr-magic-wand',
    },
  ];

  createdStoreInfo = {
    store_id: null,
    store_name: null,
  };

  createStoreForm = this.fb.group({
    storeName: [
      '',
      [StoreNamePatternValidator, Validators.required, Validators.minLength(5)],
    ],
    country: [
      { country_code: 'MY', country_name: 'Malaysia' },
      [Validators.required],
    ],
    language: [
      { language_code: 'en-US', language_name: 'English' },
      [Validators.required],
    ],
    subdomain: [
      '',
      [
        Validators.required,
        SubDomainPatternValidator,
        Validators.minLength(5),
        Validators.maxLength(24),
      ],
    ],
  });

  dealer$ = this.dealerService.getDealer();
  storeList$ = this.storeInfoService.getStoreList();

  constructor(
    private svc: StoreService,
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private router: Router,
    private fb: FormBuilder,
    private dataService: DataService,
    public utils: UtilitiesService,
    private dealerService: DealerService,
    private storeInfoService: StoreInfoService,
  ) {}

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.store_id = localStorage.getItem('store_id');
    this.initData();
    const progressStepHeight = 240;
    const progressStepWidth = 200;
    const progressStepBulletDiameter = 48;
    const connectionLineHeight =
      progressStepHeight -
      this.createStorePages.length * progressStepBulletDiameter +
      8;
    const connectionLineWidth =
      progressStepWidth -
      this.createStorePages.length * progressStepBulletDiameter +
      8;

    (document as any)?.documentElement?.style?.setProperty(
      '--create-store-rail-height',
      `${connectionLineHeight}px`,
    );
    (document as any)?.documentElement?.style?.setProperty(
      '--create-store-rail-width',
      `${connectionLineWidth}px`,
    );

    this.subdomainForm.valueChanges.subscribe((val: any) => {
      const current = (val ?? '').toString();
      if (current.trim() === '') {
        this.subdomainManuallyEdited = false;
        return;
      }

      if (this.subdomainForm.dirty && current !== this.lastAutoSubdomain) {
        this.subdomainManuallyEdited = true;
      }
    });

    this.storeNameForm.valueChanges.subscribe((val: any) => {
      if (this.subdomainManuallyEdited) {
        return;
      }

      const suggested = this.formatSubdomainFromStoreName(
        (val ?? '').toString(),
      );
      const current = (this.subdomainForm.value ?? '').toString();
      const prevAuto = this.lastAutoSubdomain;

      this.lastAutoSubdomain = suggested;

      if (!suggested) {
        return;
      }

      if (current.trim() === '' || current === prevAuto) {
        this.subdomainForm.patchValue(suggested, { emitEvent: false });
      }
    });
  }

  /**
   * Single source of truth for whether AI content generation is actually on.
   * The feature flag must be ANDed in here so the toggle state, the disabled
   * state and the outgoing request can never disagree with each other.
   */
  get aiGenerateContentEnabled(): boolean {
    return this.aiGenerateStoreContentFeatureEnabled && this.aiGenerateStoreContent;
  }

  onAiGenerateStoreContentChange(checked: boolean) {
    this.aiGenerateStoreContentTouched = true;
    this.aiGenerateStoreContent = !!checked;
    if (this.aiGenerateStoreContent) {
      this.ensureDefaultLanguageSelected();
    }
  }

  get defaultAiLanguageCode(): string {
    const formCode = this.languageForm?.value?.language_code;
    if (formCode) return formCode;
    const first = this.languageList?.[0]?.language_code;
    return first || '';
  }

  isDefaultAiLanguage(code: string): boolean {
    return !!code && code === this.defaultAiLanguageCode;
  }

  isAiLanguageSelected(code: string): boolean {
    return this.aiContentLanguageCodes.indexOf(code) !== -1;
  }

  toggleAiLanguage(code: string) {
    if (!code || this.isDefaultAiLanguage(code)) {
      return;
    }
    const idx = this.aiContentLanguageCodes.indexOf(code);
    if (idx === -1) {
      this.aiContentLanguageCodes = [...this.aiContentLanguageCodes, code];
    } else {
      this.aiContentLanguageCodes = this.aiContentLanguageCodes.filter(
        (c) => c !== code,
      );
    }
  }

  get aiGenerationEstimatedTime(): { min: number; max: number } {
    const baseMin = 5;
    const baseMax = 10;
    const perExtra = 3;
    const count = Math.max(0, (this.aiContentLanguageCodes?.length || 1) - 1);
    return {
      min: baseMin + count * perExtra,
      max: baseMax + count * perExtra,
    };
  }

  private ensureDefaultLanguageSelected() {
    const def = this.defaultAiLanguageCode;
    if (def && this.aiContentLanguageCodes.indexOf(def) === -1) {
      this.aiContentLanguageCodes = [def, ...this.aiContentLanguageCodes];
    }
  }

  onAiGenerateCardClick(event?: MouseEvent) {
    event?.preventDefault();

    const isDisabled =
      !this.aiGenerateStoreContentFeatureEnabled ||
      !this.dealerCreditsLoaded ||
      this.dealerCreditsLoading ||
      this.dealerCredits < this.dealerMinimumCredit ||
      !this.dealerCreditsValid;

    if (isDisabled) {
      return;
    }

    this.onAiGenerateStoreContentChange(!this.aiGenerateStoreContent);
  }

  private async refreshDealerCredits() {
    if (this.dealerCreditsLoading) {
      return;
    }

    this.dealerCreditsLoading = true;
    try {
      const res: any = await this.dealerService.fetchDealerInfo();
      const credits = res?.credits ?? res?.dealer_credits ?? res?.dealerCredits;
      const totalCredit =
        +(
          credits?.totalCredit ??
          credits?.total_credit ??
          credits?.total ??
          credits
        ) || 0;
      const minimumCredit =
        +(
          credits?.minimumCredit ??
          credits?.minimum_credit ??
          credits?.minimum
        ) || 10;
      const isValidRaw = credits?.is_valid ?? credits?.isValid;
      const isValid = typeof isValidRaw === 'undefined' ? true : !!isValidRaw;

      this.dealerCredits = totalCredit;
      this.dealerMinimumCredit = minimumCredit;
      this.dealerCreditsValid = isValid;
      this.dataService.updateDealerCredits(totalCredit);

      if (!this.aiGenerateStoreContentTouched) {
        this.aiGenerateStoreContent = totalCredit >= minimumCredit;
      }

      if (this.aiGenerateStoreContent) {
        this.ensureDefaultLanguageSelected();
      }

      this.dealerCreditsLoaded = true;
    } finally {
      this.dealerCreditsLoading = false;
    }
  }

  private formatSubdomainFromStoreName(name: string): string {
    const raw = (name ?? '').toString().trim().toLowerCase();
    if (!raw) {
      return '';
    }

    const ascii = raw.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

    let slug = ascii
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');

    slug = slug.slice(0, 24).replace(/-+$/, '').replace(/^-+/, '');
    return slug;
  }

  toggleAdvancedRegional() {
    this.showAdvancedRegional = !this.showAdvancedRegional;
  }

  initData() {
    console.log('INIT DATA');
    combineLatest([
      this.svc.getCountryLists(this.bearer),
      this.svc.getLanguageLists(this.bearer),
    ]).subscribe(
      (res: any) => {
        const [countryResp, languageResp] = res;
        this.countryList = countryResp?.data || [];
        this.languageList = languageResp?.data || [];

        // Default Country to Malaysia (MY)
        if (!this.countryForm.value && this.countryList.length) {
          const myCountry =
            this.countryList.find(
              (c) => c.country_code === 'MY' || c.country_code === 'MYS',
            ) || this.countryList[0];
          this.countryForm.patchValue(myCountry);
        }

        // Default Language to English (en-US / en)
        if (!this.languageForm.value && this.languageList.length) {
          const enLang =
            this.languageList.find(
              (l) => l.language_code === 'en-US' || l.language_code === 'en',
            ) || this.languageList[0];
          this.languageForm.patchValue(enLang);
        }
      },
      (error) => {
        console.log(error);
      },
      () => {
        this.isInitialLoading = false;
      },
    );
    this.dealerService.fetchDealerInfo();
    this.getDealerInfo();
  }

  closeModal() {
    this.activeModal.close('Modal Closed');
  }

  stepBackButton() {
    if (this.pageStep > 1) {
      this.pageStep--;
    }
  }

  proceedButton() {
    if (this.pageStep === 1) {
      this.storeNameForm.markAsTouched();
      this.subdomainForm.markAsTouched();

      // Ensure defaults if missing
      if (!this.countryForm.value && this.countryList.length) {
        const myCountry =
          this.countryList.find(
            (c) => c.country_code === 'MY' || c.country_code === 'MYS',
          ) || this.countryList[0] || { country_code: 'MY', country_name: 'Malaysia' };
        this.countryForm.patchValue(myCountry);
      }
      if (!this.languageForm.value && this.languageList.length) {
        const enLang =
          this.languageList.find(
            (l) => l.language_code === 'en-US' || l.language_code === 'en',
          ) || this.languageList[0] || { language_code: 'en-US', language_name: 'English' };
        this.languageForm.patchValue(enLang);
      }

      if (
        !this.storeNameForm.valid ||
        !this.subdomainForm.valid
      ) {
        return;
      }

      this.validateDomainAndProceed();
    } else if (this.pageStep === 2) {
      this.proceedCreateStore();
    }
  }

  private validateDomainAndProceed() {
    const subdomainName = this.subdomainForm;
    subdomainName.markAsTouched();

    if (
      subdomainName.invalid ||
      !subdomainName.value ||
      subdomainName.value.toString().trim() === ''
    ) {
      return;
    }

    if (this.checkingSubdomain) {
      return;
    }

    this.checkingSubdomain = true;
    this.subdomainValid = false;

    const subdomainValue = subdomainName.value.toString().trim();
    const token = localStorage.getItem('bearer');

    const extractErrorMsg = (payload: any): string | undefined => {
      const data = payload?.data;
      if (data && typeof data === 'object') {
        const firstFieldMsg = Object.values(data)[0];
        if (typeof firstFieldMsg === 'string' && firstFieldMsg) {
          return firstFieldMsg;
        }
      }
      return payload?.message;
    };

    const handleFailure = (msg: string | undefined, fallback: string) => {
      this.checkingSubdomain = false;
      this.subdomainValid = false;
      const errorMsg = msg || fallback;
      subdomainName.setErrors({ serverSide: errorMsg });
      subdomainName.markAsTouched();
      subdomainName.markAsDirty();
      this.utils.notify.showError(errorMsg);
    };

    const isSuccess = (res: any) =>
      res &&
      (res['status'] === 'success' || res['status'] === true) &&
      res['status_code'] !== false;

    this.svc.validateSubDomain(token, { subDomain: subdomainValue }).subscribe(
      (res: any) => {
        if (!isSuccess(res)) {
          handleFailure(
            extractErrorMsg(res),
            'Unable to verify subdomain availability',
          );
          return;
        }
        if (res?.data?.available === true) {
          this.checkingSubdomain = false;
          this.subdomainValid = true;
          this.pageStep = 2;
          this.refreshDealerCredits();
          if (this.aiGenerateStoreContent) {
            this.ensureDefaultLanguageSelected();
          }
        } else {
          this.checkingSubdomain = false;
          this.subdomainValid = false;
          subdomainName.setErrors({ taken: true });
          subdomainName.markAsTouched();
          subdomainName.markAsDirty();
          this.utils.notify.showError(
            this.utils.translate('Subdomain is not available.'),
          );
        }
      },
      (err: any) => {
        handleFailure(
          extractErrorMsg(err?.error),
          'Unable to verify subdomain availability',
        );
      },
    );
  }

  validateDomain() {
    const subdomainName = this.subdomainForm;
    this.subdomainValid = false;

    if (subdomainName.value.trim() === '') {
    } else {
      this.checkingSubdomain = true;

      const params = {
        subDomain: subdomainName.value?.trim(),
      };
      const token = localStorage.getItem('bearer');
      this.svc.validateSubDomain(token, params).subscribe(
        (res: any) => {
          if (!res?.data?.available) {
            this.subdomainForm.setErrors({
              taken: true,
            });
          } else {
            this.subdomainValid = true;
          }

          this.checkingSubdomain = false;
        },
        (err: any) => {
          this.subdomainForm.setErrors({
            serverSide: err?.error?.data?.subdomain,
          });

          this.subdomainValid = false;
          this.checkingSubdomain = false;
        },
      );
    }
  }

  proceedCreateStore() {
    if (this.subdomainForm.invalid) {
      this.subdomainForm.markAsTouched();
      return;
    }

    const params = {
      countryCode: this.countryForm.value?.country_code || 'MY',
      languageCode: this.languageForm.value?.language_code || 'en-US',
      store_name: this.storeNameForm.value,
      store_plan_id: this.planId,
      store_subdomain: this.subdomainForm.value,
      theme_id: 1,
      generate_ai_content: this.aiGenerateContentEnabled,
      ai_content_language_codes: this.aiGenerateContentEnabled
        ? this.aiContentLanguageCodes
        : [],
    };
    this.isLoadingCreateStore = true;
    // let newStoreInfo = null;
    const token = localStorage.getItem('bearer');
    this.svc.createMultiStore(token, params).subscribe(
      async (res: any) => {
        if (res && res['status'] == 'success' && res['status_code'] == 200) {
          const storeData = res?.data;
          this.utils.notify.showSuccess(
            this.utils.translate('New store "{0}" has been created!', [
              storeData?.store_name,
            ]),
          );
          this.pageStep = 10;

          this.createdStoreInfo = {
            store_id: storeData?.store_id,
            store_name: storeData?.store_name,
          };
          await this.storeInfoService.fetchStoreList();
          this.svc
            .publish_event({
              data: {
                d_id: this.dealerData?.dealer_id ?? '',
                type: 'Create Store Success',
                distinct_id: this.dealerData?.dealer_id ?? '',
                dealer_username: this.dealerData?.dealer_name ?? '',
                store_name: storeData?.store_name,
                default_country: params.countryCode,
                default_language: params.languageCode,
                domain: params.store_subdomain,
                enable_ai: params.generate_ai_content,
                ai_token: this.dealerCredits,
                platform: 'dashboard',
                store_count: this.storeInfoService.storeList?.length ?? 0,
              },
              application: 'dashboard',
              driver: 'mixpanel',
            })
            .subscribe();
        } else if (res && res['status'] === false) {
          const fieldErrors =
            res?.data && typeof res.data === 'object' ? res.data : {};
          const fieldErrorMsg = Object.values(fieldErrors)[0] as
            | string
            | undefined;
          const errorMsg = fieldErrorMsg || res?.message;
          if (errorMsg) {
            this.subdomainForm.setErrors({ serverSide: errorMsg });
            this.utils.notify.showError(errorMsg);
          }
        }
        this.isLoadingCreateStore = false;
        setTimeout(() => {
          this.storeList$.subscribe((res) => {
            this.dataService.emitStoreListUpdatedEvent(res);
          });
          this.dataService.emitStoreListUpdateRequestEvent();
        }, 1000);
      },
      (error) => {
        const errorMsg = error?.error?.data || [];
        this.subdomainForm.setErrors({
          serverSide: Object.values(errorMsg)[0],
        });
        this.svc
          .publish_event({
            data: {
              d_id: this.dealerData?.dealer_id ?? '',
              type: 'Create Store Fail',
              distinct_id: this.dealerData?.dealer_id ?? '',
              dealer_username: this.dealerData?.dealer_name ?? '',
              store_name: this.storeNameForm.value,
              default_country: this.countryForm.value?.country_code,
              default_language: this.languageForm.value?.language_code,
              domain: this.subdomainForm.value,
              enable_ai: this.aiGenerateStoreContent,
              platform: 'dashboard',
              store_count: this.storeInfoService.storeList?.length ?? 0,
              error_response: Object.values(errorMsg)[0] ?? '',
            },
            application: 'dashboard',
            driver: 'mixpanel',
          })
          .subscribe();
        // this.utils.notify.showError(errorMsgs);

        this.utils.notify.showError(errorMsg);
        this.isLoadingCreateStore = false;
      },
    );
  }

  getDealerInfo() {
    this.dealer$.subscribe((res) => {
      this.dealerData = res;
      this.dataService.updateDealerInfo(res);
      this.dataService.updateDealerPlan(res.dealer_plan);
      this.dataService.updateDealerCredits(res.dealer_credits);
      this.planId = +res.dealer_plan.store_plan_id;
    });
  }

  gotoCustomizeStore() {
    this.utils.switchStore(
      this.createdStoreInfo?.store_id,
      this.createdStoreInfo,
    );
    this.router.navigate(['/dashboard/design']);
    this.closeModal();
  }

  showRestrictedWordModal() {
    if (!this.restrictedWordsLoaded) {
      this.svc.getRestrictedSubdomain().subscribe((res) => {
        this.restrictedWordsLoaded = true;

        this.restrictedWords = res?.data
          .map((item) => {
            return item?.reserved_subdomain;
          })
          .sort();
      });
    }

    this.modalService.open(this.restrictedWordsModalContent, {
      centered: true,
      windowClass: 'restricted-modal-window',
      backdrop: 'static',
    });
  }

  get storeNameForm() {
    return this.createStoreForm.get('storeName');
  }

  get countryForm() {
    return this.createStoreForm.get('country');
  }

  get languageForm() {
    return this.createStoreForm.get('language');
  }

  get subdomainForm() {
    return this.createStoreForm.get('subdomain');
  }
}
